import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "store";
// material-ui
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

// third-party
import { FormattedMessage } from "react-intl";
import PerfectScrollbar from "react-perfect-scrollbar";

// project imports
import MainCard from "shared/ui/cards/MainCard";
import Transitions from "shared/ui/extended/Transitions";
import LocalizedAvatar from "shared/ui/LocalizedAvatar";
import useRestrictedFeature from "shared/hooks/useRestrictedFeature";
import BinIinField from "shared/ui/BinIinField";
import { useAuth } from "shared/hooks/useAuth";

import { EApplicationController } from "shared/types/actions";
// assets
import { IconLogout, IconSettings, IconUser } from "@tabler/icons-react";

// types
import { ThemeMode } from "shared/types/config";

//utils
import { userAvatar, userFullName } from "shared/utils/converters";

const ProfileSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logoutUser } = useAuth();
  const { mode, borderRadius } = useSelector((state) => state.config);

  const [open, setOpen] = useState(false);
  const prevOpen = useRef(open);

  const anchorRef = useRef<any>(null);

  const isClient = useRestrictedFeature([
    EApplicationController.fetch_all_applications_for_current_user,
  ]);
  const isEmployee = useRestrictedFeature([
    EApplicationController.fetch_all_applications,
  ]);

  const {
    id = "",
    firstName = "",
    lastName = "",
  } = user || { id: "", firstName: "", lastName: "" };

  const handleNavigateToProfile = () => {
    navigate(`/users/employees/employee-details/${id}`);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (
    event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent,
  ) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const avatar = userAvatar(firstName, lastName, undefined);
  const fullName = userFullName(firstName, lastName, undefined);
  const avatarFullName = { firstName, lastName, middleName: "" };
  return (
    <>
      <Chip
        sx={{
          ml: 2,
          height: "48px",
          alignItems: "center",
          borderRadius: "27px",
          transition: "all .2s ease-in-out",
          borderColor: mode === ThemeMode.DARK ? "dark.main" : "primary.light",
          bgcolor: mode === ThemeMode.DARK ? "dark.main" : "primary.light",
          '&[aria-controls="menu-list-grow"], &:hover': {
            borderColor: "primary.main",
            bgcolor: `${theme.palette.primary.main} !important`,
            color: "primary.light",
            "& svg": {
              stroke: theme.palette.primary.light,
            },
          },
          "& .MuiChip-label": {
            lineHeight: 0,
          },
        }}
        icon={
          <LocalizedAvatar
            avatar={avatar}
            fullName={avatarFullName}
            sx={{
              ...theme.typography.mediumAvatar,
              margin: "8px 0 8px 8px !important",
              cursor: "pointer",
              fontSize: 16,
            }}
          />
        }
        label={<IconSettings stroke={1.5} size='24px' />}
        variant='outlined'
        ref={anchorRef}
        aria-controls={open ? "menu-list-grow" : undefined}
        aria-haspopup='true'
        onClick={handleToggle}
        color='primary'
        aria-label='user-account'
      />
      <Popper
        placement='bottom'
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 14],
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper>
                {open && (
                  <MainCard
                    border={false}
                    elevation={16}
                    content={false}
                    boxShadow
                    shadow={theme.shadows[16]}
                  >
                    <Box sx={{ p: 2 }}>
                      <Stack alignItems='flex-start' spacing={1}>
                        <Stack direction='column' alignItems='flex-start'>
                          {user?.role && (
                            <Typography variant='subtitle2' color='info.main'>
                              {<FormattedMessage id={user.role.toString()} />}
                            </Typography>
                          )}
                          <Typography variant='h3' color='text.primary'>
                            {fullName}
                          </Typography>
                        </Stack>
                        {(isEmployee || user?.orgName) && (
                          <Stack
                            direction='column'
                            alignItems='flex-start'
                            width={320}
                          >
                            <Typography
                              variant='subtitle2'
                              color='info.main'
                              fontWeight='normal'
                              sx={{ opacity: 0.7 }}
                            >
                              <FormattedMessage id='orgName' />
                            </Typography>
                            <Typography variant='h4' color='text.secondary'>
                              {user?.orgName}
                            </Typography>
                          </Stack>
                        )}
                        {isClient && user?.orgId && (
                          <Stack direction='column' alignItems='flex-start'>
                            <Typography
                              variant='subtitle2'
                              color='info.main'
                              fontWeight='normal'
                              sx={{ opacity: 0.7 }}
                            >
                              <FormattedMessage id='org|bin|iin' />
                            </Typography>
                            <BinIinField
                              value={user?.orgId}
                              variant='h4'
                              color='text.secondary'
                            />
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                    <PerfectScrollbar
                      style={{
                        height: "100%",
                        maxHeight: "calc(100vh - 250px)",
                        overflowX: "hidden",
                      }}
                    >
                      <Box sx={{ p: 2, pt: 0 }}>
                        <Divider />
                        <List
                          component='nav'
                          sx={{
                            width: "100%",
                            maxWidth: 350,
                            minWidth: 300,
                            borderRadius: `${borderRadius}px`,
                            "& .MuiListItemButton-root": { mt: 0.5 },
                          }}
                        >
                          <Stack
                            width='100%'
                            direction='row'
                            spacing={2}
                            justifyContent='space-between'
                          >
                            <ListItemButton
                              sx={{
                                borderRadius: `${borderRadius}px`,
                                flex: 1,
                              }}
                              onClick={handleNavigateToProfile}
                            >
                              <ListItemIcon>
                                <IconUser stroke={1.5} size='20px' />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant='body2'>
                                    <FormattedMessage id='profile' />
                                  </Typography>
                                }
                              />
                            </ListItemButton>
                          </Stack>
                          <ListItemButton
                            sx={{ borderRadius: `${borderRadius}px` }}
                            onClick={logoutUser}
                          >
                            <ListItemIcon>
                              <IconLogout stroke={1.5} size='20px' />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant='body2'>
                                  <FormattedMessage id='logout' />
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        </List>
                      </Box>
                    </PerfectScrollbar>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};

export default ProfileSection;
